import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanMatch,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { AuthService } from '../_services/auth.service';
import { TokenStorageService } from '../_services/token-storage.service';
import { ADMIN_ROLES, STAFF_ROLES, UserRole, hasAnyRole } from '../_model/user-role';

@Injectable({ providedIn: 'root' })
export class AdminGuard implements CanActivate, CanActivateChild, CanMatch {
  constructor(
    private auth: AuthService,
    private router: Router,
    private tokens: TokenStorageService
  ) {}

  private checkAccess(
    redirectUrl: string,
    allowedRoles: readonly UserRole[]
  ): Observable<boolean | UrlTree> {
    const token = this.tokens.getAccessToken();
    const exp = this.tokens.getAccessExpiresAt();
    const expired = exp ? new Date(exp).getTime() <= Date.now() : false;

    if (!token || expired) {
      this.auth.forceLogout(false);
      return of(this.router.createUrlTree(
        ['/auth/login'],
        { queryParams: { returnUrl: redirectUrl } }
      ));
    }

    return this.auth.getCurrentUser().pipe(
      map(user => {
        if (user && hasAnyRole(user.role, allowedRoles)) return true;

        if (!user) {
          return this.router.createUrlTree(['/auth/login'], {
            queryParams: { returnUrl: redirectUrl }
          });
        }

        return this.router.createUrlTree(['/not-authorized'], {
          queryParams: { returnUrl: redirectUrl }
        });
      }),
      catchError(() => of(this.router.createUrlTree(['/auth/login'], {
        queryParams: { returnUrl: redirectUrl }
      })))
    );
  }

  private rolesFromData(data: Record<string, unknown> | undefined, fallback: readonly UserRole[]): readonly UserRole[] {
    const roles = data?.['roles'];
    return Array.isArray(roles) && roles.length
      ? roles as UserRole[]
      : fallback;
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.checkAccess(state.url, this.rolesFromData(route.data, STAFF_ROLES));
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.checkAccess(state.url, this.rolesFromData(childRoute.data, ADMIN_ROLES));
  }

  canMatch(route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree> {
    const url = '/' + segments.map(s => s.path).join('/');
    return this.checkAccess(url, this.rolesFromData(route.data, STAFF_ROLES));
  }
}
